import { produce } from 'immer';
import isEmpty from 'lodash/isEmpty';
import isPlainObject from 'lodash/isPlainObject';
import { useCallback, useMemo } from 'react';
import { DeepNullable, DeepWritable } from 'ts-essentials';

import {
  OrganizationStorage,
  organizationStorageApi,
  OrganizationStorageKey,
} from 'apis/organizationStorage';
import { UserStorage, userStorageApi, UserStorageKey } from 'apis/userStorage';
import { deepOmitBy } from 'utils/deepOmitBy';

/** The scope of the server storage. */
export type ServerStorageScope = 'user' | 'organization';

/** The type of the server storage based on the scope. */
export type ServerStorage<TScope extends ServerStorageScope> =
  TScope extends 'user' ? UserStorage : OrganizationStorage;

type StorageSetOptions<TEntity extends Record<string, unknown>> = {
  [Key in keyof TEntity]?: (
    draft: NonNullable<DeepWritable<DeepNullable<TEntity[Key]>>>,
  ) => void;
};

/** Options to set values in the user storage. */
type UserStorageSetOptions = StorageSetOptions<UserStorage>;

/** Options to set values in the organization storage. */
type OrganizationStorageSetOptions = StorageSetOptions<OrganizationStorage>;

/** Function to refresh the server storage data. */
type ServerStorageRefetch = () => void;

/** Function to set values in the server storage. */
type ServerStorageSet<TScope extends ServerStorageScope> = (
  patch: TScope extends 'user'
    ? UserStorageSetOptions
    : OrganizationStorageSetOptions,
) => void;

/** Function to remove values from the server storage. */
type ServerStorageRemove<TScope extends ServerStorageScope> = (
  keys: TScope extends 'user'
    ? readonly UserStorageKey[]
    : readonly OrganizationStorageKey[],
) => void;

/** Result type of the useServerStorage hook. */
export type UseServerStorageResult<TScope extends ServerStorageScope> = {
  /** The data read from server storage endpoint. */
  readonly data: ServerStorage<TScope>;
  /** Whether the initial read for the active scope is still in flight. */
  readonly isLoading: boolean;
  /** Whether a read for the active scope is in flight (initial or refetch). */
  readonly isFetching: boolean;
  /** Whether the read for the active scope has completed successfully. */
  readonly isSuccess: boolean;
  /**
   * Function to refresh the data by calling the endpoint. You may not need this
   * because data is fetched on mount.
   */
  readonly refetch: ServerStorageRefetch;
  /**
   * Function to refresh the data by calling the endpoint. You may not need this
   * because data is fetched on mount or updated by another consumer.
   *
   * To remove an entry from the data, you can set its value to null.
   *
   * @example
   *   set({
   *     keybindings: (draft) => {
   *       draft['search'] = []; // update or create "search" keybinding
   *       draft['acceptOffer'] = null; // remove "acceptOffer" keybinding
   *     },
   *   });
   */
  readonly set: ServerStorageSet<TScope>;
  /** Function to remove given keys from user storage. */
  readonly remove: ServerStorageRemove<TScope>;
};

/**
 * Wraps {@link userStorageApi} and {@link organizationStorageApi} and provides
 * their data and functions to mutate data.
 */
export const useServerStorage = <TScope extends ServerStorageScope>(
  scope: TScope,
): UseServerStorageResult<TScope> => {
  const {
    data: readUserStorageData,
    refetch: readUserStorage,
    isLoading: isUserStorageLoading,
    isFetching: isUserStorageFetching,
    isSuccess: isUserStorageSuccess,
  } = userStorageApi.useReadUserStorageQuery(
    {},
    {
      skip: scope !== 'user',
    },
  );
  const [setUserStorage] = userStorageApi.useSetUserStorageMutation();

  const {
    data: readOrganizationStorageData,
    refetch: readOrganizationStorage,
    isLoading: isOrganizationStorageLoading,
    isFetching: isOrganizationStorageFetching,
    isSuccess: isOrganizationStorageSuccess,
  } = organizationStorageApi.useReadOrganizationStorageQuery(
    {},
    {
      skip: scope !== 'organization',
    },
  );
  const [setOrganizationStorage] =
    organizationStorageApi.useSetOrganizationStorageMutation();

  const isUserScope = scope === 'user';

  const readServerStorage = isUserScope
    ? readUserStorage
    : readOrganizationStorage;
  const setServerStorage = isUserScope
    ? setUserStorage
    : setOrganizationStorage;
  const readServerStorageData = isUserScope
    ? readUserStorageData
    : readOrganizationStorageData;
  const isLoading = isUserScope
    ? isUserStorageLoading
    : isOrganizationStorageLoading;
  const isFetching = isUserScope
    ? isUserStorageFetching
    : isOrganizationStorageFetching;
  const isSuccess = isUserScope
    ? isUserStorageSuccess
    : isOrganizationStorageSuccess;

  const data = useMemo<ServerStorage<TScope>>(
    () => (readServerStorageData as ServerStorage<TScope>) ?? {},
    [readServerStorageData],
  );

  const refetch = useCallback<ServerStorageRefetch>(() => {
    readServerStorage();
  }, [readServerStorage]);

  const set = useCallback<ServerStorageSet<TScope>>(
    (patch) => {
      const payload = deepOmitBy(
        Object.fromEntries(
          Object.entries(patch).map(([key, recipeFn]) => [
            key,
            produce(data[key] ?? {}, recipeFn) as unknown,
          ]),
        ),
        (value) => value === null,
      ) as typeof data;

      Object.entries(payload).forEach(([key, value]) => {
        if (isPlainObject(value) && isEmpty(value)) {
          payload[key] = null;
        }
      });

      setServerStorage(payload);
    },
    [data, setServerStorage],
  );

  const remove = useCallback<ServerStorageRemove<TScope>>(
    (keys) => {
      const payload = Object.fromEntries(
        keys.map((key) => [key, null]),
      ) as UserStorage;

      setServerStorage(payload);
    },
    [setServerStorage],
  );

  return useMemo<UseServerStorageResult<TScope>>(
    () => ({ data, refetch, set, remove, isLoading, isFetching, isSuccess }),
    [refetch, data, remove, set, isLoading, isFetching, isSuccess],
  );
};
